import Logo from "@/components/logo";
import PaymentDone from "@/components/payment/PaymentDone";
import PaymentVerify from "@/components/pricing-plans/payment-verify";
import { routes } from "@/config/routes";
import useStepper from "@/hooks/useStepper";
import { PaymentFlow } from "@/utilities/enums/Payment";
import PaymentRepository from "@/utilities/repositories/Payment";
import { EXeroPaymentStatus, IXeroPayment } from "@/utilities/types/Payment";
import step from "@/utils/signup-step";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stepper } from "rizzui";

export default function ConfirmPaymentPage() {
  const [payment, setPayment] = useState<IXeroPayment>();
  const { state } = useLocation();
  const paymentId = state?.paymentId;
  const isAdCredit = state?.from === PaymentFlow.AD_CREDIT;
  const paymentConfirm = state?.paymentConfirm;
  const navigate = useNavigate();
  // Using useStepper instead of manual state management
  const { currentStep, goToStep } = useStepper({
    steps: isAdCredit ? step.stepAdCredit.length : step.stepRegister.length,
    initialStep: isAdCredit ? 0 : 3,
  });

  const steps = useMemo(
    () => (isAdCredit ? step.stepAdCredit : step.stepRegister),
    [isAdCredit]
  );

  const renderStepPackageLDP = () => {
    switch (currentStep) {
      case 3:
        return (
          <PaymentVerify
            isConfirm
            paymentConfirm={payment}
            typeId={payment?.packageId || ""}
            nextPage={() => goToStep(currentStep + 1)}
          />
        );
      case 4:
        return (
          <PaymentDone
            status={payment?.status || EXeroPaymentStatus.PENDING}
            payment={payment}
          />
        );
      default:
        return null;
    }
  };

  const renderStepAdCredit = () => {
    switch (currentStep) {
      case 0:
        return (
          <PaymentVerify
            paymentConfirm={paymentConfirm}
            typeId={paymentId || ""}
            nextPage={() => goToStep(currentStep + 1)}
          />
        );
      case 1:
        return (
          <PaymentVerify
            isConfirm
            paymentConfirm={paymentConfirm}
            typeId={paymentId || ""}
            nextPage={() => goToStep(currentStep + 1)}
          />
        );
      case 2:
        return (
          <PaymentDone
            status={payment?.status || EXeroPaymentStatus.PENDING}
            payment={payment}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!paymentId) {
      navigate(routes.home);
      return;
    }
    const fetchData = async () => {
      const res = await PaymentRepository.findXeroPayment(paymentId);
      setPayment(res.data);
    };
    fetchData();
  }, [paymentId, navigate]);

  return (
    <div className="container mx-auto max-w-screen-xl px-4 py-8 md:py-16">
      <div className="mb-8 flex w-full items-center justify-center">
        <Logo className="min-w-[200px]" />
      </div>
      <div className="mb-8 md:mb-12 w-full overflow-x-auto">
        <Stepper
          currentIndex={currentStep}
          className="w-full min-w-max [&_.rizzui-step-container]:!justify-center"
        >
          {steps.map((item, idx) => (
            <Stepper.Step
              key={idx}
              variant="outline"
              active={currentStep === idx}
              completed={currentStep > idx}
              title={<span className="hidden lg:inline">{item.title}</span>}
              description={
                <span className="hidden lg:inline">{item.description}</span>
              }
              icon={item.icon}
              className="text-xs lg:text-sm"
            />
          ))}
        </Stepper>
      </div>

      <div className="mb-6 text-center lg:hidden">
        <h2 className="text-xl font-bold">{steps[currentStep]?.title}</h2>
        <p className="text-sm text-gray-500">
          {steps[currentStep]?.description}
        </p>
      </div>
      {isAdCredit ? renderStepAdCredit() : renderStepPackageLDP()}
    </div>
  );
}
